import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  Copy,
  Download,
  MousePointer2,
  X,
} from 'lucide-react';
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type {
  FixListEntry,
  FixListStatus,
  Persona,
  ResearchRoomFocus,
  ResearchRoomMessage,
} from '@repo/contracts';
import { Tooltip, TooltipProvider } from '@repo/ui/tooltip';

import { trackEvent } from '../analytics/posthog';
import { setFixStatus } from '../api/canvas-captures';
import {
  catchUpRoomFixes,
  getRoomForCapture,
  roomKeys,
} from '../api/research-rooms';
import { PersonaAvatar } from '../persona/persona-avatar';
import { usePersonas } from '../persona/use-personas';
import { ANALYSIS_WIDTH } from '../research-room/analysis-card';
import { FlotoMark } from '../research-room/room-messages';
import { useResearchRoomStore } from '../store/research-room-store';
import {
  buildCombinedPrompt,
  fixListKeys,
  PrCardRow,
  type PromptPage,
  STATUS_ORDER,
  useFixListEntries,
} from './fix-list';

/** How far a press travels before it is a drag, not a click — the board's
 *  own threshold for screens. */
const DRAG_THRESHOLD_PX = 4;

/** How long a fix that just arrived, or just changed, says so. */
const FRESH_MS = 8000;

/** Below this zoom only each fix's change stays: the rest would be specks. */
const COMPACT_BELOW = 0.32;

/** Whether anyone in the room has said anything a fix could rest on. */
export function hasAnswers(messages: ResearchRoomMessage[]): boolean {
  return messages.some(
    (m) => (m.kind === 'answer' || m.kind === 'poll') && m.status === 'done',
  );
}

/**
 * The message that put a fix's place on the screen. The fix keeps only a
 * name for it; the box lives on the messages. Best is a line it cites whose
 * location chip has the same name, then any line in the room that does, and
 * failing both — the writer named the place in its own words — the place the
 * first cited answer pointed at, since that is what the fix was heard about.
 */
export function focusedMessage(
  entry: FixListEntry,
  messages: ResearchRoomMessage[],
): (ResearchRoomMessage & { focus: ResearchRoomFocus }) | null {
  const where = entry.issue.locationName.trim().toLowerCase();
  const at = (m: ResearchRoomMessage) =>
    Boolean(where) && m.focus?.label.trim().toLowerCase() === where;
  const cited = (entry.issue.evidence ?? []).flatMap(
    (cite) => messages.find((m) => m.id === cite.messageId) ?? [],
  );
  const found =
    cited.find(at) ?? messages.find(at) ?? cited.find((m) => m.focus);
  return found?.focus ? { ...found, focus: found.focus } : null;
}

/** One room's fixes: outstanding first, then in Floto's order — most backed,
 *  most severe first — then newest. */
export function roomFixes(
  entries: FixListEntry[],
  roomId: string,
): FixListEntry[] {
  return entries
    .filter((entry) => entry.roomId === roomId)
    .sort(
      (a, b) =>
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
        (a.issue.priority ?? Infinity) - (b.issue.priority ?? Infinity) ||
        b.createdAt.localeCompare(a.createdAt),
    );
}

/** What a fix says — what makes a change to it worth pointing out. */
const wordingOf = (entry: FixListEntry) =>
  `${entry.issue.description}\n${entry.issue.recommendation}`;

/**
 * Which fixes arrived, or were reworded, since this first saw the list —
 * each marked for a few seconds, so a list that changes while the room talks
 * shows where it changed. What was there on arrival is not new.
 */
export function useFreshFixes(
  entries: FixListEntry[],
  /** Whether `entries` is the list as loaded — before that, an empty list
   *  is "not here yet", and what arrives with the load is not news. */
  ready = true,
): Map<string, 'New' | 'Updated'> {
  const seen = useRef<Map<string, string> | null>(null);
  const [fresh, setFresh] = useState<Map<string, 'New' | 'Updated'>>(
    () => new Map(),
  );
  // Keyed on what the list says, not on the array: the cache hands back a
  // new array on every refetch even when nothing changed.
  const signature = entries
    .map((entry) => `${entry.id}\n${wordingOf(entry)}`)
    .join('\n\n');
  const latest = useRef(entries);
  latest.current = entries;

  useEffect(() => {
    if (!ready) return;
    const current = latest.current;
    const previous = seen.current;
    seen.current = new Map(current.map((e) => [e.id, wordingOf(e)]));
    if (!previous) return;

    const changed = new Map<string, 'New' | 'Updated'>();
    for (const entry of current) {
      const before = previous.get(entry.id);
      if (before === undefined) changed.set(entry.id, 'New');
      else if (before !== wordingOf(entry)) changed.set(entry.id, 'Updated');
    }
    if (changed.size === 0) return;

    setFresh((shown) => new Map([...shown, ...changed]));
    const timer = setTimeout(() => {
      setFresh((shown) => {
        const next = new Map(shown);
        for (const id of changed.keys()) next.delete(id);
        return next;
      });
    }, FRESH_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [ready, signature]);

  return fresh;
}

const first = (name: string) => name.split(' ')[0] ?? name;

/** Who backed a fix: their faces, overlapping, and how many. */
function Backers({
  entry,
  onOpen,
  panel,
}: {
  entry: FixListEntry;
  /** Opens the fix onto what each of them said. */
  onOpen: () => void;
  panel: Map<string, Persona>;
}) {
  const ids = [
    ...new Set((entry.issue.evidence ?? []).map((e) => e.personaId)),
  ];
  if (ids.length === 0) return null;
  return (
    <button
      className="-m-1 flex shrink-0 cursor-pointer items-center gap-2 rounded-button p-1 transition hover:bg-surface-hover"
      onClick={onOpen}
      type="button"
    >
      <span aria-hidden="true" className="flex -space-x-1.5">
        {ids.slice(0, 4).map((id) => {
          const persona = panel.get(id);
          return persona ? (
            <PersonaAvatar
              className="ring-2 ring-surface"
              key={id}
              persona={persona}
              size="sm"
            />
          ) : null;
        })}
      </span>
      <span className="text-caption text-text-muted tabular-nums">
        Raised by {ids.length} {ids.length === 1 ? 'person' : 'people'}
      </span>
    </button>
  );
}

/** Up to this many open fixes, they all start open; past it, every one
 *  starts as one line, so a longer list stays skimmable. */
const OPEN_AT_START = 2;

/** Where the agent is with a fix, said in a word on its row. */
function agentWord(entry: FixListEntry): string | null {
  switch (entry.prStatus) {
    case 'pending':
      return 'Agent working…';
    case 'opened':
      return 'PR open';
    case 'merged':
      return 'Merged';
    case 'closed':
      return 'PR closed';
    case 'failed':
      return 'Agent failed';
    default:
      return null;
  }
}

export const LEVEL: Record<string, string> = {
  high: 'High',
  low: 'Low',
  medium: 'Medium',
};

/** One open fix, opened up: what people said, and what to do about it. */
function FixDetail({
  entry,
  onStatus,
  page,
  panel,
  saving,
}: {
  entry: FixListEntry;
  page: PromptPage;
  onStatus: (status: FixListStatus) => void;
  panel: Map<string, Persona>;
  saving: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const quotes = (entry.issue.evidence ?? []).slice(0, 3);

  return (
    <div className="mt-3 space-y-4 duration-200 animate-in fade-in motion-reduce:animate-none">
      {quotes.length > 0 ? (
        <ul className="space-y-2.5">
          {quotes.map((said) => {
            const persona = panel.get(said.personaId);
            return (
              <li
                className="flex gap-2.5"
                key={`${said.personaId}:${said.messageId}`}
              >
                {persona ? (
                  <PersonaAvatar
                    className="shrink-0"
                    persona={persona}
                    size="sm"
                  />
                ) : null}
                <p className="pt-0.5 text-caption leading-relaxed text-text-muted">
                  “{said.quote}”
                  <span className="sr-only"> — {first(said.personaName)}</span>
                </p>
              </li>
            );
          })}
        </ul>
      ) : null}

      {entry.prStatus ? (
        <div className="overflow-hidden rounded-button bg-surface ring-1 ring-hairline">
          <PrCardRow entry={entry} />
        </div>
      ) : null}

      <div className="flex items-center gap-1.5">
        <button
          className="flex h-8 cursor-pointer items-center gap-1.5 rounded-button bg-ink px-3 text-caption font-semibold text-white transition hover:bg-ink/90"
          onClick={() => {
            trackEvent('fix_list_prompt_copied', { count: 1 });
            void navigator.clipboard
              .writeText(buildCombinedPrompt([entry], page))
              .then(() => {
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 2000);
              });
          }}
          type="button"
        >
          <Copy aria-hidden="true" className="size-3.5" />
          {copied ? 'Copied' : 'Copy Prompt'}
        </button>
        <span className="flex-1" />
        <button
          className="h-8 cursor-pointer rounded-button px-2.5 text-caption font-semibold text-text-primary transition hover:bg-warm disabled:cursor-default disabled:opacity-50"
          disabled={saving}
          onClick={() => {
            onStatus('wont_fix');
          }}
          type="button"
        >
          Won&apos;t Fix
        </button>
        <button
          className="h-8 cursor-pointer rounded-button bg-ink px-3 text-caption font-semibold text-white transition hover:bg-ink/90 disabled:cursor-default disabled:opacity-50"
          disabled={saving}
          onClick={() => {
            onStatus('fixed');
          }}
          type="button"
        >
          Fixed
        </button>
      </div>
    </div>
  );
}

export const RESOLVED_WORD: Record<FixListStatus, string> = {
  fix: '',
  fixed: 'Done',
  not_an_issue: 'Not an issue',
  wont_fix: "Won't fix",
};

/**
 * The room's fix list, on the board beside the screen it is about — where
 * the session write-up used to sit: the few changes the room backs most,
 * kept current every round.
 *
 * Read first, act second: each fix is one line — the change to make, why,
 * and who said it — and opens, one at a time, onto the quotes and what to do
 * with it. What has been dealt with folds away at the bottom.
 *
 * Fixes are written server-side beside each round and reach the cache over
 * the fix list's live stream, which the session page keeps open.
 */
export function FixListCard({
  anchor,
  captureId,
  docked = false,
  page = {},
  projectId,
  zoom,
}: {
  /** Its default place in board pixels — beside its screen — until it is
   *  moved. */
  anchor: { x: number; y: number };
  captureId: string;
  /** In the phone layout's Fixes tab rather than on a board: in the flow,
   *  full width, not draggable and not closable. */
  docked?: boolean;
  /** The screen's page, for the copied prompt to name. */
  page?: PromptPage;
  projectId: string;
  zoom: number;
}) {
  const { data: view } = useQuery({
    queryFn: () => getRoomForCapture(captureId),
    queryKey: roomKeys.forCapture(captureId),
  });
  const queryClient = useQueryClient();
  const all = useFixListEntries(projectId);
  const { personas } = usePersonas({ projectId });
  const panel = new Map(personas.map((persona) => [persona.id, persona]));
  const room = view?.room;
  const entries = room ? roomFixes(all ?? [], room.id) : [];
  const open = entries.filter((entry) => entry.status === 'fix');
  const resolved = entries.filter((entry) => entry.status !== 'fix');
  const fresh = useFreshFixes(entries, Boolean(room) && all !== undefined);
  // A short list starts open — with only a couple, there is nothing to skim
  // past. Once a third arrives they all fold to one line each, so the list
  // keeps to the space it has. Opening or closing one by hand sticks for it.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const toggle = (id: string, open: boolean) => {
    setToggled((current) => ({ ...current, [id]: open }));
  };
  const [showResolved, setShowResolved] = useState(false);
  const thinking = room?.status === 'thinking';
  const answered = view ? hasAnswers(view.messages) : false;
  // The place a fix is about, lit and panned to on the board — the same as
  // the location chip on the room's own lines.
  const showSpotlight = useResearchRoomStore((s) => s.showSpotlight);

  // Its own object on the board, like a Figma layer: moving its screen
  // leaves it where it is, and it moves only when dragged itself — press
  // anywhere on it and drag. A press that does not travel stays a click (a
  // row still opens, a button still presses), and one that does never also
  // clicks what it started on.
  const closed = useResearchRoomStore((s) => s.closedFixCards[captureId]);
  const closeCard = useResearchRoomStore((s) => s.closeFixCard);
  const placed = useResearchRoomStore((s) => s.fixCardPositions[captureId]);
  const position = placed ?? anchor;
  const moveCard = useResearchRoomStore((s) => s.moveFixCard);
  const keepPositions = useResearchRoomStore((s) => s.keepFixCardPositions);
  const drag = useRef<{
    origin: { x: number; y: number };
    pointer: { x: number; y: number };
    /** How far it has moved, in board pixels. */
    delta: { x: number; y: number };
    moved: boolean;
  } | null>(null);
  const cardRef = useRef<HTMLElement>(null);
  const justDragged = useRef(false);
  const [dragging, setDragging] = useState(false);
  const endDrag = () => {
    const done = drag.current;
    drag.current = null;
    if (!done?.moved) return;
    setDragging(false);
    // One write, at the end. The card was moved by `translate` on the way
    // (below); the layout effect clears that once this position renders, in
    // the same frame, so it never flickers back.
    moveCard(captureId, {
      x: done.origin.x + done.delta.x,
      y: done.origin.y + done.delta.y,
    });
    keepPositions();
    // The click that follows this pointerup is the drag's, not a press.
    justDragged.current = true;
    setTimeout(() => {
      justDragged.current = false;
    }, 0);
  };
  useLayoutEffect(() => {
    if (!drag.current && cardRef.current) cardRef.current.style.translate = '';
  }, [position.x, position.y, dragging]);

  // A room from before the fix list existed: catch it up once, so its
  // conversation so far is on the list. Every round after keeps it current.
  const catchUp = useMutation({
    mutationFn: (roomId: string) => catchUpRoomFixes(roomId),
  });
  const caughtUp = useRef<string | null>(null);
  const { mutate: runCatchUp } = catchUp;
  const roomId = room?.id;
  const needsCatchUp = room !== undefined && room.fixesRound === undefined;
  useEffect(() => {
    if (!roomId || !needsCatchUp || !answered || thinking) return;
    if (caughtUp.current === roomId) return;
    caughtUp.current = roomId;
    runCatchUp(roomId);
  }, [answered, needsCatchUp, roomId, runCatchUp, thinking]);

  const triage = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FixListStatus }) =>
      setFixStatus(id, status),
    onSuccess: (updated) => {
      // One event for every way a status changes — Fixed, Won't Fix, and
      // Undo (back to `fix`).
      trackEvent('fix_triaged', {
        severity: updated.issue.severity,
        status: updated.status,
      });
      queryClient.setQueryData<FixListEntry[]>(
        fixListKeys.project(updated.projectId),
        (current) =>
          current?.map((entry) =>
            entry.id === updated.id
              ? { ...entry, status: updated.status }
              : entry,
          ),
      );
    },
  });

  if (!room || (closed && !docked)) return null;

  const compact = zoom < COMPACT_BELOW;
  const writing = thinking || catchUp.isPending;

  return (
    <TooltipProvider>
      <article
        ref={cardRef}
        aria-label="Fix list"
        className={
          docked
            ? 'overflow-hidden rounded-card bg-surface shadow-soft ring-1 ring-hairline'
            : [
                // Only the shadow eases: with no property named, `duration-500` (the
                // fade-in's) animated every move too — a half-second lag behind
                // the pointer.
                'absolute touch-none overflow-hidden rounded-card bg-surface ring-1 transition-shadow duration-500 animate-in fade-in slide-in-from-left-2 select-none motion-reduce:animate-none',
                // Figma's hover outline, and a lift while it moves.
                dragging
                  ? 'z-10 cursor-grabbing shadow-modal ring-highlight will-change-transform'
                  : 'cursor-grab shadow-floating ring-hairline hover:ring-2 hover:ring-highlight/70',
              ].join(' ')
        }
        data-fix-card={captureId}
        {...(docked
          ? {}
          : {
              onClickCapture: (event: ReactMouseEvent) => {
                if (!justDragged.current) return;
                event.preventDefault();
                event.stopPropagation();
              },
              // Back beside its screen.
              onDoubleClick: (event: ReactMouseEvent) => {
                if (
                  event.target instanceof Element &&
                  event.target.closest('button')
                ) {
                  return;
                }
                moveCard(captureId, null);
                keepPositions();
              },
              onLostPointerCapture: endDrag,
              onPointerDown: (event: ReactPointerEvent) => {
                // The board never sees a press on the card: it is not empty board.
                event.stopPropagation();
                if (event.button !== 0) return;
                if (
                  event.target instanceof Element &&
                  event.target.closest('a, input, textarea, select')
                ) {
                  return;
                }
                drag.current = {
                  delta: { x: 0, y: 0 },
                  moved: false,
                  origin: position,
                  pointer: { x: event.clientX, y: event.clientY },
                };
              },
              onPointerMove: (event: ReactPointerEvent<HTMLElement>) => {
                const from = drag.current;
                if (!from) return;
                const dx = event.clientX - from.pointer.x;
                const dy = event.clientY - from.pointer.y;
                if (!from.moved) {
                  if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
                  from.moved = true;
                  setDragging(true);
                  // Taken only once it is a drag, so a plain click still lands on
                  // what it pressed.
                  if ('setPointerCapture' in event.currentTarget) {
                    event.currentTarget.setPointerCapture(event.pointerId);
                  }
                }
                // Straight onto the element, not through React or the store: a
                // re-render of the whole list on every pointer move is what made
                // it lag behind the pointer. `translate` is composited — no layout,
                // no repaint. The board is scaled; a screen pixel is 1/zoom board
                // pixels.
                from.delta = { x: dx / zoom, y: dy / zoom };
                event.currentTarget.style.translate = `${String(from.delta.x)}px ${String(from.delta.y)}px`;
              },
              onPointerUp: endDrag,
              style: {
                left: position.x,
                top: position.y,
                width: ANALYSIS_WIDTH,
              },
            })}
      >
        <header
          className="border-b border-hairline px-5 pt-4 pb-3"
          title={
            docked ? undefined : 'Drag to move · double-click to put it back'
          }
        >
          <div className="flex items-center gap-2">
            <FlotoMark />
            <h3 className="!m-0 flex-1 !text-ui !font-normal !text-text-muted">
              <span className="font-semibold text-text-primary">Floto</span> ·
              Fix list
            </h3>
            {writing ? (
              <span
                aria-live="polite"
                className="animate-text-shimmer bg-gradient-to-r from-text-muted via-text-primary to-text-muted bg-[length:200%_100%] bg-clip-text text-caption text-transparent motion-reduce:animate-none motion-reduce:bg-none motion-reduce:text-text-muted"
              >
                Listening…
              </span>
            ) : null}
            {entries.length > 0 ? (
              <Tooltip label="Download as PDF">
                <a
                  aria-label="Download as PDF"
                  className="flex size-7 items-center justify-center rounded-button text-text-muted transition hover:bg-warm hover:text-text-primary"
                  href={`/sessions/${room.sessionId}/fixes/${captureId}?print=1`}
                  onClick={() => {
                    trackEvent('fix_list_pdf_opened', {
                      count: entries.length,
                    });
                  }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Download aria-hidden="true" className="size-4" />
                </a>
              </Tooltip>
            ) : null}
            {docked ? null : (
              <Tooltip label="Close the fix list">
                <button
                  aria-label="Close the fix list"
                  className="-mr-1.5 flex size-7 cursor-pointer items-center justify-center rounded-button text-text-muted transition hover:bg-warm hover:text-text-primary"
                  onClick={() => {
                    trackEvent('fix_list_closed', { count: entries.length });
                    closeCard(captureId);
                  }}
                  type="button"
                >
                  <X aria-hidden="true" className="size-4" />
                </button>
              </Tooltip>
            )}
          </div>
          {/* Its own leading: the root's 145% resolves to a fixed 26px that
              every unset paragraph inherits — double spacing at this size. */}
          <p className="mt-1 text-ui leading-snug text-text-muted">
            Actionable fixes from your room, updated as the conversation
            develops.
          </p>
        </header>

        {open.length === 0 && resolved.length === 0 ? (
          // The design's empty state, before the first question and after
          // it alike: until there is a fix, the way to one is to ask.
          <div className="flex flex-col items-center gap-[10px] px-[30px] pt-[28px] pb-[33px] text-center">
            <p className="font-heading text-heading-sm font-bold tracking-tight text-text-primary">
              Your first fix starts with a conversation
            </p>
            <p className="max-w-[288px] text-subtitle leading-relaxed text-text-secondary">
              Ask the room a question about your page. Floto will turn the
              feedback into suggested fixes and add them here.
            </p>
          </div>
        ) : (
          <>
            {open.length > 0 ? (
              <ol className="divide-y divide-hairline">
                {open.map((entry) => {
                  const isOpen =
                    !compact &&
                    (toggled[entry.id] ?? open.length <= OPEN_AT_START);
                  const word = agentWord(entry);
                  const change = entry.issue.recommendation.trim();
                  const why = entry.issue.description.trim();
                  const where =
                    entry.issue.locationName &&
                    entry.issue.locationName !== 'Page-level'
                      ? entry.issue.locationName
                      : null;
                  const pointed =
                    where && room && view
                      ? focusedMessage(entry, view.messages)
                      : null;
                  return (
                    <li className="px-5 py-4" key={entry.id}>
                      <button
                        aria-expanded={compact ? undefined : isOpen}
                        className="block w-full cursor-pointer text-left"
                        disabled={compact}
                        onClick={() => {
                          if (!isOpen) {
                            trackEvent('fix_expanded', {
                              severity: entry.issue.severity,
                            });
                          }
                          toggle(entry.id, !isOpen);
                        }}
                        type="button"
                      >
                        <span className="flex items-start gap-2">
                          <span
                            className={[
                              'block min-w-0 flex-1 font-semibold text-text-primary',
                              compact
                                ? 'text-card-title leading-tight'
                                : 'text-ui-strong leading-snug',
                            ].join(' ')}
                          >
                            {change || why}
                            {fresh.has(entry.id) ? (
                              <span className="ml-2 inline-block size-2 rounded-full bg-idea align-middle">
                                <span className="sr-only">
                                  {fresh.get(entry.id)}
                                </span>
                              </span>
                            ) : null}
                          </span>
                          {/* That it opens, and which way it is now. */}
                          {compact ? null : (
                            <ChevronDown
                              aria-hidden="true"
                              className={[
                                'mt-0.5 size-4 shrink-0 text-text-muted transition-transform duration-200 motion-reduce:transition-none',
                                isOpen ? 'rotate-180' : '',
                              ].join(' ')}
                            />
                          )}
                        </span>
                        {!compact && change && why && change !== why ? (
                          <span
                            className={[
                              'mt-2 block text-caption leading-relaxed text-text-secondary',
                              isOpen ? '' : 'line-clamp-3',
                            ].join(' ')}
                          >
                            {entry.issue.severity === 'Critical' ? (
                              <span className="font-semibold text-critical">
                                Blocker:{' '}
                              </span>
                            ) : null}
                            {why}
                          </span>
                        ) : null}
                      </button>

                      {/* Who raised it and where it is, open or closed —
                          the same row both ways, so opening never leaves a
                          gap where the faces were. */}
                      {!compact ? (
                        <div className="mt-3 flex items-center gap-2">
                          <Backers
                            entry={entry}
                            onOpen={() => {
                              toggle(entry.id, true);
                            }}
                            panel={panel}
                          />
                          <span className="flex-1" />
                          {word ? (
                            <span className="rounded-pill bg-warm-alt px-2 py-0.5 text-tag font-medium text-text-secondary">
                              {word}
                            </span>
                          ) : null}
                          {/* No canvas to show it on in the docked card: a plain label. */}
                          {where && pointed && room && !docked ? (
                            <Tooltip label="Show on the canvas">
                              <button
                                className="flex max-w-1/2 cursor-pointer items-center gap-1 rounded-pill bg-where-soft px-2.5 py-1 text-tag font-medium text-where transition hover:brightness-95 active:scale-[0.97]"
                                onClick={() => {
                                  trackEvent('fix_spotlighted', {
                                    severity: entry.issue.severity,
                                  });
                                  showSpotlight({
                                    auto: false,
                                    captureId:
                                      pointed.focus.captureId ?? room.captureId,
                                    focus: pointed.focus,
                                    speaker:
                                      pointed.speakerKind === 'persona' &&
                                      pointed.speakerId
                                        ? (panel.get(pointed.speakerId) ?? null)
                                        : null,
                                  });
                                }}
                                type="button"
                              >
                                <MousePointer2
                                  aria-hidden="true"
                                  className="size-3 shrink-0"
                                />
                                <span className="truncate">{where}</span>
                              </button>
                            </Tooltip>
                          ) : where ? (
                            <span className="flex max-w-1/2 items-center gap-1 rounded-pill bg-where-soft px-2.5 py-1 text-tag font-medium text-where">
                              <MousePointer2
                                aria-hidden="true"
                                className="size-3 shrink-0"
                              />
                              <span className="truncate">{where}</span>
                            </span>
                          ) : null}
                        </div>
                      ) : null}

                      {isOpen ? (
                        <FixDetail
                          entry={entry}
                          page={page}
                          onStatus={(status) => {
                            triage.mutate({ id: entry.id, status });
                          }}
                          panel={panel}
                          saving={
                            triage.isPending && triage.variables.id === entry.id
                          }
                        />
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            ) : (
              // Everything dealt with: said plainly, with what was done
              // underneath — each one a press away from coming back.
              // Laid out by the column, not by margins: the global `p
              // { margin: 0 }` outranks Tailwind's margin utilities, which
              // left this copy off-centre.
              <div className="flex flex-col items-center px-6 pt-7 pb-5 text-center">
                <span className="mb-3 flex size-12 items-center justify-center rounded-full bg-tag-success text-success-ink">
                  <Check aria-hidden="true" className="size-5" />
                </span>
                <p className="text-ui-strong leading-snug font-semibold text-text-primary">
                  Nothing left to fix
                </p>
                <p className="max-w-64 pt-1 text-caption leading-snug text-text-secondary">
                  Every issue from this session is handled. Ask the room another
                  question to find more.
                </p>
              </div>
            )}

            {triage.error ? (
              <p className="px-5 pt-3 text-caption text-critical" role="alert">
                {triage.error instanceof Error
                  ? triage.error.message
                  : 'Could not save that'}
              </p>
            ) : null}

            {resolved.length > 0 && !compact ? (
              <div className="px-4 pb-4">
                {open.length > 0 ? (
                  <button
                    aria-expanded={showResolved}
                    className="cursor-pointer px-1 pt-3 text-caption font-medium text-text-muted transition hover:text-text-primary"
                    onClick={() => {
                      trackEvent('fix_resolved_toggled', {
                        count: resolved.length,
                        on: !showResolved,
                      });
                      setShowResolved(!showResolved);
                    }}
                    type="button"
                  >
                    {showResolved ? 'Hide' : 'Show'} resolved ({resolved.length}
                    )
                  </button>
                ) : null}
                {open.length === 0 || showResolved ? (
                  <ul className="mt-2 space-y-2">
                    {resolved.map((entry) => (
                      <li
                        className="flex items-center gap-2.5 rounded-button bg-tag-success py-2 pr-2 pl-3"
                        key={entry.id}
                      >
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-success-ink text-white">
                          <Check aria-hidden="true" className="size-3" />
                        </span>
                        <span className="min-w-0 flex-1 text-caption leading-snug font-medium text-text-primary">
                          {entry.issue.recommendation.trim() ||
                            entry.issue.description}
                        </span>
                        <span className="shrink-0 text-tag font-semibold text-success-ink">
                          {RESOLVED_WORD[entry.status]}
                        </span>
                        <button
                          className="h-7 shrink-0 cursor-pointer rounded-button bg-surface px-2.5 text-tag font-semibold text-text-primary shadow-soft transition hover:bg-warm disabled:opacity-50"
                          disabled={
                            triage.isPending && triage.variables.id === entry.id
                          }
                          onClick={() => {
                            triage.mutate({ id: entry.id, status: 'fix' });
                          }}
                          type="button"
                        >
                          Undo
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <div className="h-1" />
            )}
          </>
        )}
      </article>
    </TooltipProvider>
  );
}
